import {
  RadioGroup,
  RadioGroupItem,
  FormItem,
  FormLabel,
  FormControl,
  useFormField,
  RequiredIndicator,
} from '~/components'
import { cn } from '~/lib/utils'
import { formGridCell } from './FormFieldCell'
import { CheckCircleIcon } from '@heroicons/react/20/solid'

// Full literal class strings so Tailwind's JIT picks them up, and so the
// responsive variant survives `cn` — `'2xl:'` and `'grid-cols-3'` passed as
// separate arguments left a dangling prefix and let tailwind-merge resolve
// grid-cols-2 vs grid-cols-3 in favour of three columns at every breakpoint.
const GRID_COLUMNS_MAP: Record<number, string> = {
  2: 'grid-cols-2',
  3: 'grid-cols-2 2xl:grid-cols-3',
}

export type RadioOption = {
  label: string
  value: string | number
  subtitle?: string
}

export type StyledRadioGroupProps = {
  label: string
  options: readonly RadioOption[]
  value: string | number
  onValueChange: (value: string) => void
  className?: string
  maxColumns?: number
  required?: boolean
}

export const StyledRadioGroup = ({
  label,
  options,
  value,
  onValueChange,
  className,
  maxColumns = 2,
  required,
}: StyledRadioGroupProps) => {
  const fieldsetId = `radio-group-${label.toLowerCase().replace(/\s+/g, '-')}`
  const { error } = useFormField()

  return (
    // The shared two-row grid cell (see `formGridCell`), built by hand rather than
    // via `FormFieldCell` because a group needs a <legend>, not a <label>.
    // `contents` on the fieldset is what makes it work: a <legend> is never a grid
    // item of its own fieldset, so the fieldset box has to step out of the way for
    // the grid to see it.
    <FormItem className={cn(formGridCell, 'gap-y-4')}>
      <fieldset className="contents">
        <legend
          id={`${fieldsetId}-legend`}
          className={cn(
            // Not a flex container: the asterisk is an inline span meant to follow the last word.
            // As a flex item it became a sibling of the whole text block and got pushed to the
            // container's right edge, floating away from the end of a label that wraps.
            'text-sm font-medium',
            error ? 'text-red-500 dark:text-red-900' : 'text-heading'
          )}
        >
          {label}
          {required && <RequiredIndicator />}
        </legend>
        <div className="flex flex-col">
          <FormControl>
            <RadioGroup
              onValueChange={onValueChange}
              defaultValue={String(value)}
              className={cn(
                'grid',
                GRID_COLUMNS_MAP[maxColumns] || 'grid-cols-2',
                'gap-3 lg:gap-6'
              )}
              aria-labelledby={`${fieldsetId}-legend`}
              role="radiogroup"
            >
              {options.map((option, index) => (
                <FormItem key={option.value} className="flex flex-col space-y-0">
                  <FormControl className="peer">
                    <RadioGroupItem
                      value={String(option.value)}
                      id={`${fieldsetId}-option-${index}`}
                      aria-describedby={`${fieldsetId}-option-${index}-label`}
                      className="absolute opacity-0 w-0 h-0 pointer-events-none"
                    />
                  </FormControl>
                  <FormLabel
                    htmlFor={`${fieldsetId}-option-${index}`}
                    id={`${fieldsetId}-option-${index}-label`}
                    className={cn(
                      'flex flex-col w-full min-h-full text-sm items-center justify-center rounded-lg p-4 bg-slate-50 font-medium shadow-sm relative border-2 border-slate-200 text-slate-700 text-center transition-all duration-300 ease-in-out md:hover:bg-slate-100 md:hover:border-slate-300 md:hover:text-slate-800 cursor-pointer focus-within-ring-brand',
                      value === option.value &&
                        'border-blue-300 bg-blue-100/60 text-slate-800 md:hover:text-slate-800 md:hover:bg-blue-100/80 md:hover:border-blue-400 transition-all duration-300 ease-in-out',
                      'peer-focus-ring-brand'
                    )}
                  >
                    <span className="whitespace-normal break-words text-center">
                      {option.label}
                    </span>
                    {option.subtitle && (
                      <span className="whitespace-normal break-words text-center text-xs text-slate-500 mt-1 font-normal">
                        {option.subtitle}
                      </span>
                    )}
                    {value === option.value && (
                      <CheckCircleIcon className="absolute top-2 right-2 h-4 w-4 text-slate-600 animate-in fade-in-0 zoom-in-50 duration-200" />
                    )}
                  </FormLabel>
                </FormItem>
              ))}
            </RadioGroup>
          </FormControl>
          <RadioGroupErrorMessage />
        </div>
      </fieldset>
    </FormItem>
  )
}

const RadioGroupErrorMessage = () => {
  const { error, formMessageId } = useFormField()

  const errorMessage = error?.message || '\u00A0'

  return (
    <p
      id={formMessageId}
      className={cn(
        'text-sm font-medium text-red-500 dark:text-red-900 mt-1 col-span-full min-h-[3rem]',
        !error && 'invisible'
      )}
    >
      {errorMessage}
    </p>
  )
}
